import { useEffect, useRef, useCallback } from "react";
import { usePost } from "./usePost";
import { AsyncJobStatusResponse } from "../types/compassAITypes";
import { useApp, useJobs } from "../store/AppProvider";
import { useToast } from "../store/ToastProvider";

const SYNC_INTERVAL_MS = 30_000;
const MAX_CONSECUTIVE_ERRORS = 3;

export function useJobsPoller(): void {
  const { app } = useApp();
  const { syncFromServer } = useJobs();
  const { addToast } = useToast();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isFetchingRef = useRef(false);
  const consecutiveErrors = useRef(0);

  const { sendData: fetchJobsRaw } = usePost<
    AsyncJobStatusResponse[],
    undefined
  >();

  const token = app.authorisation.token;

  const fetchJobs = useCallback(async (): Promise<void> => {
    if (!token || isFetchingRef.current) return;

    isFetchingRef.current = true;
    try {
      const data = await fetchJobsRaw("AI_GET_JOBS", undefined);
      if (data) {
        syncFromServer(data);
        consecutiveErrors.current = 0;
      }
    } catch {
      consecutiveErrors.current++;
      if (consecutiveErrors.current >= MAX_CONSECUTIVE_ERRORS) {
        addToast("Failed to sync jobs — check your connection", "error");
        consecutiveErrors.current = 0;
      }
    } finally {
      isFetchingRef.current = false;
    }
  }, [token, fetchJobsRaw, syncFromServer, addToast]);

  // Start / stop interval based on auth
  useEffect(() => {
    if (!token) return;

    fetchJobs();
    intervalRef.current = setInterval(fetchJobs, SYNC_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchJobs, token]);

  // Pause when tab is hidden
  useEffect(() => {
    const handleVisibility = (): void => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else if (token) {
        fetchJobs();
        intervalRef.current = setInterval(fetchJobs, SYNC_INTERVAL_MS);
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [fetchJobs, token]);
}
